import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  Switch,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { Add as AddIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  HUBSPOT_CONFIG_API_BASE,
  HUBSPOT_OAUTH_URL_API,
  HUBSPOT_PROPERTIES_API,
} from 'application/config/apiConfig';
import { useListStageCategories } from 'application/hooks/useListStageCategories';
import { useListStages } from 'application/hooks/useListStages';
import {
  pickBestConfig,
  patchIntegrationConfig,
  deleteIntegrationConfig,
  listIntegrationConfigs,
} from '../utils/integrationConfigUtils';
import {
  findContactsCategory,
  extractFlexFields,
} from '../utils/flexFieldUtils';
import { ConfirmationModal } from './ConfirmationModal';
import { MappingRow } from './shared/MappingRow';
import { whiteSelectSx, whiteSelectMenuProps } from './shared/selectStyles';

export const HubSpotSetup: React.FC = () => {
  const navigate = useNavigate();

  // State
  const [isInitialLoading, setIsInitialLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [saveMsg, setSaveMsg] = React.useState<string | null>(null);
  const [existingIntegrationId, setExistingIntegrationId] = React.useState<
    string | null
  >(null);
  const [isActive, setIsActive] = React.useState<boolean | null>(null);
  const [hasOAuthTokens, setHasOAuthTokens] = React.useState(false);
  const [isConnecting, setIsConnecting] = React.useState(false);
  const [isToggling, setIsToggling] = React.useState(false);
  const [isDisableOpen, setIsDisableOpen] = React.useState(false);
  const [isRemoveOpen, setIsRemoveOpen] = React.useState(false);
  const [contactsCategoryID, setContactsCategoryID] = React.useState<
    string | null
  >(null);
  const [isEditing, setIsEditing] = React.useState(false);

  // Stage mapping state
  const [paivStages, setPaivStages] = React.useState<
    Array<{ id: string; title: string; sequence?: number }>
  >([]);
  const [stageMappings, setStageMappings] = React.useState<
    Record<string, string>
  >({});
  const [loadedStageMappings, setLoadedStageMappings] = React.useState<
    Record<string, string>
  >({});
  const [isSaving, setIsSaving] = React.useState(false);

  // Field mapping state
  const [fieldMappings, setFieldMappings] = React.useState<
    Record<string, string>
  >({});
  const [loadedFieldMappings, setLoadedFieldMappings] = React.useState<
    Record<string, string>
  >({});
  const [hubspotProperties, setHubspotProperties] = React.useState<
    Array<{ name: string; label: string; type: string; groupName: string }>
  >([]);
  const [isFetchingProperties, setIsFetchingProperties] = React.useState(false);
  const [propertiesFetched, setPropertiesFetched] = React.useState(false);
  const [availableFlexFields, setAvailableFlexFields] = React.useState<
    Array<{ name: string; label: string }>
  >([]);
  const [enabledFieldMappings, setEnabledFieldMappings] = React.useState<
    Array<string>
  >([]);
  const [loadedEnabledFieldMappings, setLoadedEnabledFieldMappings] =
    React.useState<Array<string>>([]);
  const [addFieldDialogOpen, setAddFieldDialogOpen] = React.useState(false);

  // Sync options state
  const [syncSalesRep, setSyncSalesRep] = React.useState(true);
  const [loadedSyncSalesRep, setLoadedSyncSalesRep] = React.useState(true);

  // HubSpot lead statuses (common defaults)
  const hubspotLeadStatuses = [
    { id: 'NEW', label: 'New' },
    { id: 'OPEN', label: 'Open' },
    { id: 'IN_PROGRESS', label: 'In Progress' },
    { id: 'OPEN_DEAL', label: 'Open Deal' },
    { id: 'UNQUALIFIED', label: 'Unqualified' },
    { id: 'ATTEMPTED_TO_CONTACT', label: 'Attempted to Contact' },
    { id: 'CONNECTED', label: 'Connected' },
    { id: 'BAD_TIMING', label: 'Bad Timing' },
  ];

  // Load categories and stages
  const { data: categories = [] } = useListStageCategories();
  const { data: stages = [] } = useListStages();

  useEffect(() => {
    const contacts = findContactsCategory(categories);
    setContactsCategoryID(contacts?.id ?? null);
    setAvailableFlexFields(extractFlexFields(contacts?.flexAttributes));
  }, [categories]);

  useEffect(() => {
    setPaivStages(
      stages.map((s) => ({
        id: s.id,
        title: s.title,
        sequence: s.sequence,
      }))
    );
  }, [stages]);

  // Load existing HubSpot integration config
  useEffect(() => {
    (async () => {
      const orgID = (window as any)?.me?.orgID;
      if (!orgID) {
        setIsInitialLoading(false);
        return;
      }

      try {
        const res = await listIntegrationConfigs(
          HUBSPOT_CONFIG_API_BASE,
          orgID
        );
        const items = res.success ? res.items ?? [] : [];
        const picked = pickBestConfig(items);

        if (!picked) {
          setExistingIntegrationId(null);
          setIsActive(null);
          setHasOAuthTokens(false);
          setIsInitialLoading(false);
          return;
        }

        setExistingIntegrationId(picked.id ?? null);
        setIsActive(Boolean(picked.isActive));

        // Check if OAuth was actually completed (tokens are redacted to "****" in responses)
        const co = picked.configOptions ?? {};
        const tokensPresent = Boolean(
          co.tokenExpiry || co.accessToken || co.refreshToken
        );
        setHasOAuthTokens(tokensPresent);
        const mappings =
          co.stageMapping && typeof co.stageMapping === 'object'
            ? (co.stageMapping as Record<string, string>)
            : {};
        setStageMappings(mappings);
        setLoadedStageMappings(mappings);

        // Load field mappings
        const fMappings =
          co.fieldMapping && typeof co.fieldMapping === 'object'
            ? (co.fieldMapping as Record<string, string>)
            : {};
        setFieldMappings(fMappings);
        setLoadedFieldMappings(fMappings);
        const enabledFields = Object.keys(fMappings);
        setEnabledFieldMappings(enabledFields);
        setLoadedEnabledFieldMappings(enabledFields);

        // Load sync options
        const syncRep =
          co.syncSalesRep !== undefined ? Boolean(co.syncSalesRep) : true;
        setSyncSalesRep(syncRep);
        setLoadedSyncSalesRep(syncRep);
      } catch (err) {
        console.error('[HubSpotSetup] Error loading integration:', err);
      } finally {
        setIsInitialLoading(false);
      }
    })();
  }, []);

  const handleConnect = async () => {
    setIsConnecting(true);
    setError(null);

    try {
      const orgID = (window as any)?.me?.orgID;
      if (!orgID) throw new Error('Organization ID not found');

      let configId = existingIntegrationId;

      // If no existing config, create one first
      if (!configId) {
        if (!contactsCategoryID) {
          throw new Error(
            'No contacts category found. Please set up your stages first.'
          );
        }

        const headers = await getAuthHeaders();
        const createRes = await fetch(HUBSPOT_CONFIG_API_BASE, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            orgID,
            categoryID: contactsCategoryID,
            configOptions: {},
          }),
        });

        const createData = await createRes.json();
        if (!createRes.ok || !createData.success) {
          throw new Error(createData.error || 'Failed to create config');
        }

        configId = createData.id;
        // Don't update existingIntegrationId here — that would flash the
        // "pending OAuth" screen before the redirect happens.
      }

      // Get the OAuth URL
      const headers = await getAuthHeaders();
      const returnUrl = encodeURIComponent(
        `${window.location.origin}/settings/integrations/hubspot/setup`
      );
      const oauthRes = await fetch(
        `${HUBSPOT_OAUTH_URL_API}?configId=${configId}&returnUrl=${returnUrl}`,
        {
          method: 'GET',
          headers,
        }
      );

      const oauthData = await oauthRes.json();
      if (!oauthRes.ok || !oauthData.success) {
        throw new Error(oauthData.error || 'Failed to get OAuth URL');
      }

      // Redirect to HubSpot OAuth — keep isConnecting=true so the UI
      // stays on the "Connecting..." state until the browser navigates away.
      window.location.href = oauthData.oauthUrl;
      // Don't hit finally — keep spinner visible during redirect
    } catch (err: any) {
      console.error('[HubSpotSetup] Connect error:', err);
      setError(err.message || 'Failed to initiate HubSpot connection');
      setIsConnecting(false);
    }
  };

  const handleSaveMappings = async () => {
    if (!existingIntegrationId) return;

    setIsSaving(true);
    setError(null);

    try {
      const headers = await getAuthHeaders();
      const res = await fetch(
        `${HUBSPOT_CONFIG_API_BASE}/${existingIntegrationId}`,
        {
          method: 'PATCH',
          headers,
          body: JSON.stringify({
            configOptions: {
              stageMapping: stageMappings,
              fieldMapping: fieldMappings,
              syncSalesRep,
            },
          }),
        }
      );

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to save configuration');
      }

      setLoadedStageMappings({ ...stageMappings });
      setLoadedFieldMappings({ ...fieldMappings });
      setLoadedEnabledFieldMappings([...enabledFieldMappings]);
      setLoadedSyncSalesRep(syncSalesRep);
      setSaveMsg('Configuration saved successfully!');
      setIsEditing(false);
    } catch (err: any) {
      setError(err.message || 'Failed to save configuration');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setStageMappings({ ...loadedStageMappings });
    setFieldMappings({ ...loadedFieldMappings });
    setEnabledFieldMappings([...loadedEnabledFieldMappings]);
    setSyncSalesRep(loadedSyncSalesRep);
    setAddFieldDialogOpen(false);
    setIsEditing(false);
    setError(null);
    setSaveMsg(null);
  };

  const handleEnable = async () => {
    if (!existingIntegrationId) return;
    setIsToggling(true);
    setError(null);
    setSaveMsg(null);
    try {
      const res = await patchIntegrationConfig(
        HUBSPOT_CONFIG_API_BASE,
        existingIntegrationId,
        { isActive: true }
      );
      if (res.success) {
        setIsActive(true);
        setSaveMsg('HubSpot integration enabled successfully');
      } else {
        throw new Error(res.error || 'Failed to enable integration');
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to enable integration');
    } finally {
      setIsToggling(false);
    }
  };

  const handleDisable = async () => {
    if (!existingIntegrationId) return;
    setIsToggling(true);
    setError(null);
    setSaveMsg(null);
    try {
      const res = await patchIntegrationConfig(
        HUBSPOT_CONFIG_API_BASE,
        existingIntegrationId,
        { isActive: false }
      );
      if (res.success) {
        setIsActive(false);
        setSaveMsg('HubSpot integration disabled successfully');
      } else {
        throw new Error(res.error || 'Failed to disable integration');
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to disable integration');
    } finally {
      setIsToggling(false);
      setIsDisableOpen(false);
    }
  };

  const handleRemove = async () => {
    if (!existingIntegrationId) return;
    setIsToggling(true);
    try {
      const res = await deleteIntegrationConfig(
        HUBSPOT_CONFIG_API_BASE,
        existingIntegrationId
      );
      if (res.success) {
        setExistingIntegrationId(null);
        setIsActive(null);
        setHasOAuthTokens(false);
        setStageMappings({});
        setLoadedStageMappings({});
        setFieldMappings({});
        setLoadedFieldMappings({});
        setEnabledFieldMappings([]);
        setLoadedEnabledFieldMappings([]);
        setSyncSalesRep(true);
        setLoadedSyncSalesRep(true);
        setPropertiesFetched(false);
        setHubspotProperties([]);
        setIsEditing(false);
        setSaveMsg('HubSpot integration removed successfully');
      } else {
        throw new Error(res.error || 'Failed to remove integration');
      }
    } catch (err) {
      setError('Failed to remove integration');
    } finally {
      setIsToggling(false);
      setIsRemoveOpen(false);
    }
  };

  const isConnected = isActive === true && hasOAuthTokens;
  const isDisabled =
    isActive === false && existingIntegrationId !== null && hasOAuthTokens;
  const isPendingOAuth = existingIntegrationId !== null && !hasOAuthTokens;

  const fetchHubSpotProperties = async () => {
    if (propertiesFetched || isFetchingProperties || !existingIntegrationId)
      return;
    setIsFetchingProperties(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(
        `${HUBSPOT_PROPERTIES_API}?configId=${existingIntegrationId}`,
        { method: 'GET', headers }
      );
      const data = await res.json();
      if (res.ok && data.success) {
        setHubspotProperties(data.properties || []);
        setPropertiesFetched(true);
      } else {
        console.error('[HubSpotSetup] Failed to fetch properties:', data.error);
      }
    } catch (err) {
      console.error('[HubSpotSetup] Error fetching properties:', err);
    } finally {
      setIsFetchingProperties(false);
    }
  };

  // Fetch HubSpot properties on load when connected and field mappings exist
  useEffect(() => {
    if (
      (isConnected || isDisabled) &&
      existingIntegrationId &&
      Object.keys(loadedFieldMappings).length > 0 &&
      !propertiesFetched
    ) {
      fetchHubSpotProperties();
    }
  }, [isConnected, isDisabled, existingIntegrationId, loadedFieldMappings]);

  if (isInitialLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          mt: 10,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3, mt: 10 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 4,
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          HubSpot Integration Setup
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {isDisabled && !isEditing && (
            <Button
              variant="contained"
              color="success"
              onClick={handleEnable}
              disabled={isToggling}
            >
              {isToggling ? 'Enabling...' : 'Enable Integration'}
            </Button>
          )}
          {isDisabled && !isEditing && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => setIsRemoveOpen(true)}
              disabled={isToggling}
            >
              Remove Integration
            </Button>
          )}
          {isConnected && !isEditing && (
            <Button
              variant="outlined"
              color="warning"
              onClick={() => setIsDisableOpen(true)}
              disabled={isToggling}
            >
              Disable Integration
            </Button>
          )}
          {isConnected && !isEditing && (
            <Button variant="outlined" onClick={() => setIsEditing(true)}>
              Edit Configuration
            </Button>
          )}
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      {saveMsg && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {saveMsg}
        </Alert>
      )}

      {/* Connection Section */}
      {!isConnected && !isDisabled && !isPendingOAuth && (
        <Box
          sx={{
            p: 4,
            textAlign: 'center',
            backgroundColor: 'grey.50',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Connect your HubSpot account
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mb: 3, maxWidth: 500, mx: 'auto' }}
          >
            Click the button below to securely connect your HubSpot account via
            OAuth. You&apos;ll be redirected to HubSpot to authorize the
            connection.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={handleConnect}
            disabled={isConnecting}
            sx={{ minWidth: 200 }}
          >
            {isConnecting ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                Connecting...
              </>
            ) : (
              'Connect'
            )}
          </Button>
        </Box>
      )}

      {/* Pending OAuth — config exists but tokens were never received */}
      {isPendingOAuth && (
        <Box
          sx={{
            p: 4,
            textAlign: 'center',
            backgroundColor: 'grey.50',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'warning.main',
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Connection incomplete
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mb: 3, maxWidth: 500, mx: 'auto' }}
          >
            It looks like the HubSpot authorization wasn&apos;t completed. Click
            below to try again.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={handleConnect}
            disabled={isConnecting}
            sx={{ minWidth: 200 }}
          >
            {isConnecting ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                Connecting...
              </>
            ) : (
              'Connect'
            )}
          </Button>
        </Box>
      )}

      {/* Connected / Disabled status */}
      {(isConnected || isDisabled) && (
        <Box sx={{ mb: 3 }}>
          <Alert severity={isConnected ? 'success' : 'warning'} sx={{ mb: 3 }}>
            {isConnected
              ? 'Your HubSpot account is connected and syncing.'
              : 'Your HubSpot integration is currently disabled. Enable it to resume syncing.'}
          </Alert>

          {/* Stage Mappings */}
          {paivStages.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Stage Mappings
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Map your Paiv stages to HubSpot lead statuses. When a deal moves
                to a Paiv stage, the corresponding HubSpot lead status will be
                updated.
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {[...paivStages]
                  .sort((a, b) => {
                    const seqA = a.sequence ?? Number.MAX_SAFE_INTEGER;
                    const seqB = b.sequence ?? Number.MAX_SAFE_INTEGER;
                    if (seqA !== seqB) return seqA - seqB;
                    return a.title.localeCompare(b.title);
                  })
                  .map((paivStage) => (
                    <MappingRow
                      key={paivStage.id}
                      leftLabel="Paiv Stage"
                      leftValue={paivStage.title}
                      rightLabel="HubSpot Lead Status"
                      rightContent={
                        <FormControl fullWidth size="small">
                          <Select
                            value={stageMappings[paivStage.id] || ''}
                            onChange={(e) => {
                              setStageMappings((prev) => ({
                                ...prev,
                                [paivStage.id]: e.target.value,
                              }));
                            }}
                            disabled={!isEditing}
                            displayEmpty
                            sx={whiteSelectSx}
                            MenuProps={whiteSelectMenuProps}
                          >
                            <MenuItem value="">
                              <em>Select Lead Status</em>
                            </MenuItem>
                            {hubspotLeadStatuses.map((status) => (
                              <MenuItem key={status.id} value={status.id}>
                                {status.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      }
                    />
                  ))}
              </Box>
            </Box>
          )}

          {/* Field Mappings */}
          {(isEditing || Object.keys(loadedFieldMappings).length > 0) && (
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Box>
                  <Typography variant="h6" sx={{ mb: 0.5 }}>
                    Field Mappings
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Map Paiv fields to HubSpot contact properties for outbound
                    sync.
                  </Typography>
                </Box>
                {isEditing && (
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      if (!propertiesFetched) fetchHubSpotProperties();
                      setAddFieldDialogOpen(true);
                    }}
                  >
                    Add Field Mapping
                  </Button>
                )}
              </Box>

              {enabledFieldMappings.length === 0 ? (
                <Box
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    backgroundColor: 'grey.50',
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No field mappings configured. Click &quot;Add Field
                    Mapping&quot; to map a Paiv field to a HubSpot property.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {isFetchingProperties && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <CircularProgress size={16} />
                      <Typography variant="body2" color="text.secondary">
                        Loading HubSpot properties...
                      </Typography>
                    </Box>
                  )}
                  {enabledFieldMappings.map((fieldName) => {
                    const field = availableFlexFields.find(
                      (f) =>
                        f.name === fieldName ||
                        f.name.toLowerCase() === fieldName.toLowerCase()
                    );
                    const displayLabel =
                      field?.label && field.label !== fieldName
                        ? `${field.label} (${fieldName})`
                        : fieldName;
                    const mappedValue = fieldMappings[fieldName] || '';
                    return (
                      <MappingRow
                        key={fieldName}
                        leftLabel="Paiv Field"
                        leftValue={displayLabel}
                        rightLabel="HubSpot Property"
                        rightContent={
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 1,
                              alignItems: 'flex-start',
                            }}
                          >
                            <FormControl fullWidth size="small">
                              <Select
                                value={mappedValue}
                                onChange={(e) => {
                                  setFieldMappings((prev) => ({
                                    ...prev,
                                    [fieldName]: e.target.value,
                                  }));
                                }}
                                disabled={!isEditing}
                                displayEmpty
                                onOpen={() => {
                                  if (!propertiesFetched)
                                    fetchHubSpotProperties();
                                }}
                                sx={whiteSelectSx}
                                MenuProps={whiteSelectMenuProps}
                              >
                                <MenuItem value="">
                                  <em>Select HubSpot Property</em>
                                </MenuItem>
                                {hubspotProperties.map((prop) => (
                                  <MenuItem key={prop.name} value={prop.name}>
                                    {prop.label} ({prop.name})
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            {isEditing && (
                              <Tooltip title="Remove field mapping">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    const newMappings = { ...fieldMappings };
                                    delete newMappings[fieldName];
                                    setFieldMappings(newMappings);
                                    setEnabledFieldMappings((prev) =>
                                      prev.filter((n) => n !== fieldName)
                                    );
                                  }}
                                  sx={{
                                    mt: 0.5,
                                    color: 'error.main',
                                    '&:hover': {
                                      backgroundColor: 'error.light',
                                      color: 'error.dark',
                                    },
                                  }}
                                >
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        }
                      />
                    );
                  })}
                </Box>
              )}
            </Box>
          )}

          {/* Sync Options */}
          {(isEditing || isConnected || isDisabled) && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Sync Options
              </Typography>
              <Box
                sx={{
                  p: 2,
                  backgroundColor: 'grey.50',
                  borderRadius: 1,
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={syncSalesRep}
                      onChange={(e) => setSyncSalesRep(e.target.checked)}
                      disabled={!isEditing}
                    />
                  }
                  label="Sync sales rep / owner"
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ ml: 6 }}
                >
                  When enabled, the Paiv deal owner will be matched to a HubSpot
                  owner by email and set as the contact owner.
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Sticky Save/Cancel Footer */}
      {isEditing && (isConnected || isDisabled) && (
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            backgroundColor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            p: 2,
            mt: 3,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
            zIndex: 10,
          }}
        >
          <Button variant="outlined" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveMappings}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Configuration'}
          </Button>
        </Box>
      )}

      {/* Add Field Mapping Dialog */}
      <Dialog
        open={addFieldDialogOpen}
        onClose={() => setAddFieldDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Field Mapping</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a Paiv field to map to a HubSpot property.
          </Typography>
          {(() => {
            // Build a combined list of all known fields:
            // 1. From availableFlexFields (category flex attributes)
            // 2. From loadedFieldMappings keys (saved config — covers seeded defaults)
            const knownFields = new Map<string, string>();
            for (const f of availableFlexFields) {
              knownFields.set(f.name, f.label);
            }
            for (const key of Object.keys(loadedFieldMappings)) {
              if (!knownFields.has(key)) {
                // Convert camelCase to readable label as fallback
                const label = key
                  .replace(/([A-Z])/g, ' $1')
                  .replace(/^./, (s) => s.toUpperCase())
                  .trim();
                knownFields.set(key, label);
              }
            }

            const selectableFields = Array.from(knownFields.entries())
              .filter(([name]) => !enabledFieldMappings.includes(name))
              .map(([name, label]) => ({ name, label }));

            return selectableFields.length > 0 ? (
              <List>
                {selectableFields.map((field) => (
                  <ListItem
                    key={field.name}
                    button
                    onClick={() => {
                      setEnabledFieldMappings([
                        ...enabledFieldMappings,
                        field.name,
                      ]);
                      setAddFieldDialogOpen(false);
                    }}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <ListItemText
                      primary={field.label}
                      secondary={`Field: ${field.name}`}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box sx={{ mt: 1 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  All known fields have been added. Enter a field name manually
                  to add a custom mapping.
                </Typography>
                <FormControl fullWidth size="small">
                  <input
                    type="text"
                    placeholder="e.g. firstName, phoneNumber"
                    style={{
                      padding: '10px 14px',
                      fontSize: '14px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      width: '100%',
                      boxSizing: 'border-box',
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const value = (
                          e.target as HTMLInputElement
                        ).value.trim();
                        if (value && !enabledFieldMappings.includes(value)) {
                          setEnabledFieldMappings([
                            ...enabledFieldMappings,
                            value,
                          ]);
                          setAddFieldDialogOpen(false);
                        }
                      }
                    }}
                  />
                </FormControl>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddFieldDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Modals */}
      <ConfirmationModal
        open={isDisableOpen}
        onCancel={() => setIsDisableOpen(false)}
        onConfirm={handleDisable}
        title="Disable HubSpot Integration"
        message="Are you sure you want to disable this HubSpot integration? This will stop syncing data between HubSpot and Paiv."
        confirmText="Disable"
        severity="warning"
      />

      <ConfirmationModal
        open={isRemoveOpen}
        onCancel={() => setIsRemoveOpen(false)}
        onConfirm={handleRemove}
        title="Remove HubSpot Integration"
        message="Are you sure you want to permanently remove this HubSpot integration? This action cannot be undone. You will need to re-authorize with HubSpot to reconnect."
        confirmText="Remove"
        severity="error"
      />
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

async function getAuthHeaders(): Promise<Record<string, string>> {
  const session = await (await import('aws-amplify')).Auth.currentSession();
  const token = session.getIdToken().getJwtToken();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}
